import * as joint from '@clientio/rappid';

import { COMMENT_BADGE_CLASS } from '../base/comment-badge.util';

//This service is generated by AI

export class FlowchartExportUtil {
  private static logoUrl = '/assets/img/logos/treetop_icon_blau.png'; // Using the Treetop logo from assets/logos
  private static readonly svgPaintAttributes = ['fill', 'stroke', 'color', 'stop-color', 'flood-color', 'lighting-color'];

  // Only the CSS properties that affect SVG paint / text layout. Inlining the full
  // `getComputedStyle` list (~300 props per element) blows up memory for big guidelines.
  private static readonly inlinedCssProperties = [
    'fill',
    'fill-opacity',
    'stroke',
    'stroke-opacity',
    'stroke-width',
    'stroke-dasharray',
    'stroke-linecap',
    'stroke-linejoin',
    'color',
    'opacity',
    'visibility',
    'display',
    'font-family',
    'font-size',
    'font-weight',
    'font-style',
    'text-anchor',
    'dominant-baseline',
    'alignment-baseline',
    'letter-spacing',
    'word-spacing',
    'white-space',
  ];

  // Rich text inside foreignObject also depends on HTML layout rules. Keep this
  // separate so ordinary SVG nodes do not carry unnecessary box-model styles.
  private static readonly inlinedHtmlCssProperties = [
    'line-height',
    'box-sizing',
    'margin-top',
    'margin-right',
    'margin-bottom',
    'margin-left',
    'padding-top',
    'padding-right',
    'padding-bottom',
    'padding-left',
    'text-align',
    'text-indent',
    'text-decoration',
    'vertical-align',
    'list-style-type',
    'list-style-position',
    'border-collapse',
    'border-spacing',
    'border-top',
    'border-right',
    'border-bottom',
    'border-left',
  ];

  /**
   * Sanitizes a string to be used as a filename
   * @param filename The string to sanitize
   * @returns A sanitized string safe for use as a filename
   */
  public static sanitizeFilename(filename: string | null | undefined): string {
    // Replace invalid characters with a dash
    return (filename ?? '')
      .replace(/[<>:"/\\|?*]/g, '-') // Replace invalid characters with dash
      .replace(/\s+/g, '-') // Replace spaces with dash
      .replace(/-+/g, '-') // Replace multiple dashes with single dash
      .replace(/^-+|-+$/g, '') // Remove dashes from start and end
      .substring(0, 255); // Limit length to 255 characters (common filename length limit)
  }

  /**
   * Prepares the canvas with the flowchart image and optionally watermarks
   * @param paper The JointJS paper instance containing the flowchart
   * @param includeWatermarks Whether to include watermark text and logo
   * @param onCanvasReady Callback function that receives the prepared canvas
   */
  private static prepareCanvas(
    paper: joint.dia.Paper,
    includeWatermarks: boolean,
    onCanvasReady: (canvas: HTMLCanvasElement, width: number, height: number, padding: number) => void,
  ): void {
    if (!paper) {
      return;
    }

    // Get the paper's SVG element
    const svgElement = paper.svg;
    const svgParent = svgElement.parentElement;
    if (!svgParent || !svgElement.isConnected) {
      throw new Error('Cannot export flowchart: SVG is not attached to the document.');
    }

    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Get the paper's content bounding box
    const bbox = paper.getContentBBox();

    // Add padding to the dimensions
    const padding = 40;
    const width = bbox.width + padding * 2;
    const height = bbox.height + padding * 2;

    // Set canvas dimensions
    canvas.width = width;
    canvas.height = height;

    // Scale up the canvas for better quality
    const scale = 2; // Scale factor for better quality
    canvas.width = width * scale;
    canvas.height = height * scale;
    if (ctx) {
      ctx.scale(scale, scale);
    }

    // Create an image from the SVG
    const img = new Image();

    // Clone the SVG to avoid modifying the original
    const svgClone = svgElement.cloneNode(true) as SVGElement;

    // Strip comment decoration before resolving colors, otherwise selected-comment
    // strokes become baked into the export as inline styles.
    svgClone.querySelectorAll(`.${COMMENT_BADGE_CLASS}`).forEach((badge) => badge.remove());
    [svgClone, ...Array.from(svgClone.querySelectorAll('[class]'))].forEach((element) => {
      Array.from(element.classList).forEach((className) => {
        if (className.startsWith('tt-comments-') || className === 'tt-comment-element--selected' || className === 'tt-personal-note') {
          element.classList.remove(className);
        }
      });
    });

    // Measure the clean copy under the same theme/custom-color ancestors. Keep it
    // offscreen and remove it synchronously; never change the live paper's classes.
    svgClone.style.position = 'fixed';
    svgClone.style.left = '-100000px';
    svgParent.appendChild(svgClone);
    try {
      FlowchartExportUtil.inlineComputedStyles(svgClone, svgClone);
    } finally {
      svgClone.remove();
    }

    // Serialize the SVG with inlined styles
    const svgData = new XMLSerializer().serializeToString(svgClone);

    // Add viewBox to ensure proper scaling
    const svgWithViewBox = svgData.replace('<svg', `<svg viewBox="${bbox.x - padding} ${bbox.y - padding} ${width} ${height}"`);

    // Convert SVG to a data URL
    const svgDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgWithViewBox)));

    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Fill background with white
      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);

        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0, width, height);

        if (includeWatermarks) {
          // Add the watermark text
          ctx.font = '12px Roboto';
          ctx.fillStyle = '#666666';
          const watermarkText = 'Created with Treetop Smart Guidelines';
          const textX = padding;
          const textY = height - padding / 2;
          ctx.fillText(watermarkText, textX, textY);

          // Add logo watermark
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.onload = () => {
            // Calculate logo dimensions (adjust size as needed)
            const logoWidth = 40;
            const logoHeight = (logoWidth * logoImg.height) / logoImg.width;

            // Position logo in bottom right corner with less padding
            const logoX = width - logoWidth - padding / 2; // Reduced padding from right
            const logoY = height - logoHeight - padding / 2;

            // Draw logo with some transparency
            ctx.globalAlpha = 0.7;
            ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
            ctx.globalAlpha = 1.0;

            onCanvasReady(canvas, width, height, padding);
          };

          logoImg.src = FlowchartExportUtil.logoUrl;
        } else {
          // No watermarks, call callback directly
          onCanvasReady(canvas, width, height, padding);
        }
      }
    };

    img.src = svgDataUrl;
  }

  /**
   * Exports the flowchart as a PNG image
   * @param paper The JointJS paper instance containing the flowchart
   * @param fileName The title of the flowchart
   */
  public static exportAsPng(paper: joint.dia.Paper, fileName = `treetop-flowchart-${new Date().toISOString()}`): void {
    FlowchartExportUtil.prepareCanvas(paper, true, (canvas, width, height, padding) => {
      try {
        // Convert to PNG and trigger download with higher quality
        const pngUrl = canvas.toDataURL('image/png', 1.0); // Maximum quality
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `${FlowchartExportUtil.sanitizeFilename(fileName)}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } catch (error) {
        console.error('Failed to export flowchart:', error);
      }
    });
  }

  /**
   * Converts the flowchart paper to a PNG blob for PDF export
   * @param paper The JointJS paper instance containing the flowchart
   * @param callback Callback function that receives the PNG blob
   */
  public static convertPaperToPngBlob(paper: joint.dia.Paper, callback: (blob: Blob) => void): void {
    FlowchartExportUtil.prepareCanvas(paper, false, (canvas) => {
      try {
        // Convert to PNG blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.error('Failed to convert canvas to blob');
              return;
            }
            callback(blob);
          },
          'image/png',
          1.0,
        );
      } catch (error) {
        console.error('Failed to export flowchart:', error);
      }
    });
  }

  private static inlineComputedStyles(sourceSvg: SVGElement, targetSvg: SVGElement): void {
    const sourceElements = [sourceSvg, ...Array.from(sourceSvg.getElementsByTagName('*'))];
    const targetElements = [targetSvg, ...Array.from(targetSvg.getElementsByTagName('*'))];

    targetElements.forEach((element, index) => {
      const sourceElement = sourceElements[index];
      if (!sourceElement) {
        return;
      }

      const computedStyle = window.getComputedStyle(sourceElement);
      const properties = sourceElement.closest('foreignObject')
        ? [...FlowchartExportUtil.inlinedCssProperties, ...FlowchartExportUtil.inlinedHtmlCssProperties]
        : FlowchartExportUtil.inlinedCssProperties;
      const styleString = properties
        .map((prop) => {
          const value = computedStyle.getPropertyValue(prop);
          return value ? `${prop}: ${value}` : '';
        })
        .filter(Boolean)
        .join('; ');

      if (styleString) {
        element.setAttribute('style', styleString);
      }

      FlowchartExportUtil.inlineResolvedPaintAttributes(element, computedStyle);

      if (element.tagName.toLowerCase() === 'text') {
        const fill = computedStyle.getPropertyValue('fill') || computedStyle.getPropertyValue('color');
        if (fill) {
          element.setAttribute('fill', fill);
        }
      }
    });
  }

  private static inlineResolvedPaintAttributes(element: Element, computedStyle: CSSStyleDeclaration): void {
    FlowchartExportUtil.svgPaintAttributes.forEach((attribute) => {
      const attributeValue = element.getAttribute(attribute);
      if (!attributeValue?.includes('var(')) {
        return;
      }

      const computedValue = computedStyle.getPropertyValue(attribute);
      if (computedValue) {
        element.setAttribute(attribute, computedValue);
      }
    });
  }
}
